<template>
	<div
		ref="listEl"
		data-testid="help-assistant-message-list"
		class="help-message-list"
		@scroll="handleScroll"
	>
		<div
			v-if="isEmpty"
			class="help-message-list__empty"
		>
			<p class="help-message-list__greeting">
				Hi! Ask me anything about the Syrenis platform.
			</p>
			<p
				data-testid="help-assistant-disclaimer"
				class="help-message-list__disclaimer"
			>
				AI-generated content. Verify important information.
			</p>
		</div>

		<template v-else>
			<div
				v-for="(message, index) in messages"
				:key="message.uid"
				:class="[
					'help-message-list__turn',
					message.role === 'user' ? 'help-message-list__turn--user' : 'help-message-list__turn--assistant'
				]"
			>
				<div
					:class="[
						'help-message-list__bubble',
						message.role === 'user' ? 'help-message-list__bubble--user' : 'help-message-list__bubble--assistant'
					]"
				>
					<span
						v-if="isTyping(message)"
						data-testid="help-assistant-typing"
						aria-label="Assistant is typing"
						class="help-message-list__typing"
					>
						<span class="help-message-list__dot" />
						<span class="help-message-list__dot" />
						<span class="help-message-list__dot" />
					</span>
					<!-- renderBody uses markdown-it's html:false default (raw HTML in the source is
					escaped, never passed through) as the first line of defense; v-dompurify-html
					sanitises the rendered markdown output as the second (AB#26561 PR #2723 review). -->
					<div
						v-else-if="message.role === 'assistant' && !message.isError"
						v-dompurify-html="renderBody(message.content)"
						class="help-message-list__markdown"
					/>
					<template v-else>
						{{ message.content }}
					</template>
				</div>

				<span
					v-if="message.role === 'user' && message.timestamp"
					:data-testid="`help-assistant-timestamp-${index}`"
					class="help-message-list__timestamp"
				>
					{{ formatMessageTimestamp(message.timestamp) }}
				</span>

				<button
					v-if="canRetry(message, index)"
					type="button"
					:data-testid="`help-assistant-retry-${index}`"
					:disabled="streaming"
					class="help-message-list__retry"
					@click="$emit('retry', { index })"
				>
					Retry
				</button>

				<div
					v-if="showActionsRow(message)"
					class="help-message-list__actions"
				>
					<button
						type="button"
						:data-testid="`help-assistant-copy-${index}`"
						aria-label="Copy response"
						class="help-message-list__icon-button"
						@click="handleCopy(index, message.uid, message.content)"
					>
						<LucideIcon
							:name="copiedUid === message.uid ? 'Check' : 'Copy'"
							:size="16"
						/>
					</button>
					<button
						type="button"
						:data-testid="`help-assistant-thumbs-up-${index}`"
						aria-label="Good response"
						:aria-pressed="ratings[message.id] === 'up'"
						:disabled="Boolean(ratings[message.id]) || message.id === undefined"
						class="help-message-list__icon-button"
						@click="handleRate(message.id, 'up')"
					>
						<LucideIcon
							name="ThumbsUp"
							:size="16"
						/>
					</button>
					<button
						type="button"
						:data-testid="`help-assistant-thumbs-down-${index}`"
						aria-label="Bad response"
						:aria-pressed="ratings[message.id] === 'down'"
						:disabled="Boolean(ratings[message.id]) || message.id === undefined"
						class="help-message-list__icon-button"
						@click="handleRate(message.id, 'down')"
					>
						<LucideIcon
							name="ThumbsDown"
							:size="16"
						/>
					</button>
					<button
						v-if="index === messages.length - 1"
						type="button"
						:data-testid="`help-assistant-regenerate-${index}`"
						aria-label="Regenerate response"
						class="help-message-list__icon-button"
						@click="$emit('regenerate')"
					>
						<LucideIcon
							name="RefreshCw"
							:size="16"
						/>
					</button>
					<span
						v-if="message.timestamp"
						:data-testid="`help-assistant-timestamp-${index}`"
						class="help-message-list__timestamp"
					>
						{{ formatMessageTimestamp(message.timestamp) }}
					</span>
				</div>

				<div
					v-if="showFollowups(message, index)"
					class="help-message-list__chips"
				>
					<button
						v-for="(question, qIndex) in messageFollowups(message.content)"
						:key="question"
						type="button"
						:data-testid="`help-assistant-followup-${qIndex}`"
						class="help-message-list__chip"
						@click="$emit('followup', { text: question })"
					>
						{{ question }}
					</button>
				</div>
			</div>
		</template>

		<div
			v-if="streaming && toolActivity"
			data-testid="help-assistant-tool-activity"
			class="help-message-list__tool-activity"
		>
			{{ toolActivity }}
		</div>

		<!-- The one live region for this transcript (AB#26561 PR #2723 review) - nested live
		regions (this used to also live on the scrolling container and on the tool-activity hint
		above) have undefined behaviour across screen readers, so there is exactly one, and the
		tool-activity text is folded into it rather than announced separately. -->
		<div
			role="status"
			data-testid="help-assistant-live-status"
			class="sr-only"
		>
			{{ liveStatus }}
		</div>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import { splitFollowups, formatMessageTimestamp } from './utils/chat-core.js'
import { renderMarkdown } from './utils/markdown.js'
import { shouldStickToBottom } from './utils/message-list-core.js'

/**
 * Presentational chat transcript for the help assistant (AB#26561).
 *
 * Props: `messages` ({role, content, isError, id, uid, timestamp}[]), `streaming` (Boolean),
 * `toolActivity` (String|null), `ratings` (Object, keyed by server-assigned messageId).
 *
 * Emits:
 * - `retry` `{ index }` - the message index of the error turn whose Retry was clicked. Any
 *   error turn is retryable (not just the last one), so the payload is required: more than one
 *   error bubble can render a Retry link at once, and the panel needs to know which fired.
 * - `rate` `{ messageId, rating }`
 * - `copy` `{ index }`
 * - `regenerate` (no payload - always applies to the last assistant message)
 * - `followup` `{ text }`
 */
export default {
	name: 'help-message-list',
	components: {
		LucideIcon
	},
	props: {
		messages: {
			type: Array,
			default: () => []
		},
		streaming: {
			type: Boolean,
			default: false
		},
		toolActivity: {
			type: String,
			default: null
		},
		ratings: {
			type: Object,
			default: () => ({})
		}
	},
	data () {
		return {
			copiedUid: null,
			copiedTimeout: null,
			stickToBottom: true,
			// The transcript's one live region (AB#26561 PR #2723 review) - see the watchers
			// below for when this changes.
			liveStatus: ''
		}
	},
	computed: {
		isEmpty () {
			return this.messages.length === 0
		}
	},
	watch: {
		messages () {
			this.scrollIfStuck()
		},
		streaming (isStreaming, wasStreaming) {
			if (isStreaming) {
				this.liveStatus = this.toolActivity || 'Assistant is responding'
			} else if (wasStreaming) {
				this.liveStatus = 'Response complete'
			}
		},
		toolActivity (activity) {
			this.scrollIfStuck()
			if (this.streaming) this.liveStatus = activity || 'Assistant is responding'
		}
	},
	mounted () {
		this.scrollToBottom()
	},
	beforeDestroy () {
		if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
	},
	methods: {
		formatMessageTimestamp,
		messageFollowups (content) {
			return splitFollowups(content).followups
		},
		renderBody (content) {
			return renderMarkdown(splitFollowups(content).body)
		},
		isTyping (message) {
			return message.role === 'assistant' && !message.isError && message.content === '' && this.streaming
		},
		canRetry (message, index) {
			const previous = this.messages[index - 1]
			return Boolean(message.isError) && Boolean(previous) && previous.role === 'user'
		},
		showActionsRow (message) {
			return message.role === 'assistant' && !message.isError && !this.streaming && message.content !== ''
		},
		showFollowups (message, index) {
			return message.role === 'assistant' &&
				!message.isError &&
				!this.streaming &&
				index === this.messages.length - 1 &&
				this.messageFollowups(message.content).length > 0
		},
		handleRate (messageId, rating) {
			if (messageId === undefined || this.ratings[messageId]) return
			this.$emit('rate', { messageId, rating })
		},
		async handleCopy (index, uid, content) {
			const text = splitFollowups(content).body
			try {
				await navigator.clipboard.writeText(text)
			} catch {
				return
			}
			this.copiedUid = uid
			if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
			this.copiedTimeout = setTimeout(() => {
				this.copiedUid = null
			}, 2000)
			this.$emit('copy', { index })
		},
		handleScroll () {
			const el = this.$refs.listEl
			if (!el) return
			this.stickToBottom = shouldStickToBottom({
				scrollHeight: el.scrollHeight,
				scrollTop: el.scrollTop,
				clientHeight: el.clientHeight
			})
		},
		scrollIfStuck () {
			if (this.stickToBottom) this.scrollToBottom()
		},
		scrollToBottom () {
			this.$nextTick(() => {
				const el = this.$refs.listEl
				if (el) el.scrollTop = el.scrollHeight
			})
		}
	}
}
</script>

<style lang="scss" scoped>
.help-message-list {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  overflow-y: auto;

  &__empty {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__greeting {
    margin: 0;
    padding: 8px 12px;
    font-size: 0.875rem;
    color: var(--foreground, #4A5568);
    background-color: var(--muted, #ECECED);
    border-radius: var(--radius, 8px);
  }

  &__disclaimer {
    margin: 0;
    padding: 0 4px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);
  }

  &__turn {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &--user {
      align-items: flex-end;
    }

    &--assistant {
      align-items: flex-start;
    }
  }

  &__bubble {
    max-width: 85%;
    padding: 8px 12px;
    font-size: 0.875rem;
    line-height: 1.5;
    word-break: break-word;
    border-radius: var(--radius, 8px);

    &--user {
      color: var(--primary-foreground, #FFFFFF);
      background-color: var(--primary, #5C7B9D);
    }

    &--assistant {
      color: var(--foreground, #4A5568);
      background-color: var(--muted, #ECECED);
    }
  }

  &__markdown {
    ::v-deep p {
      margin: 0.5em 0;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    ::v-deep ul,
    ::v-deep ol {
      margin: 0.5em 0;
      padding-left: 1.25em;
    }

    ::v-deep pre {
      padding: 8px;
      overflow-x: auto;
      font-size: 0.8125rem;
      background-color: var(--background, #F5F5F5);
      border: 1px solid var(--border, #E0E2E5);
      border-radius: var(--radius-sm, 4px);
    }

    ::v-deep code {
      font-size: 0.8125rem;
    }
  }

  &__typing {
    display: inline-flex;
    gap: 4px;
    align-items: center;
    padding: 4px 0;
  }

  &__dot {
    width: 6px;
    height: 6px;
    background-color: var(--muted-foreground, #666E7A);
    border-radius: 50%;
    animation: help-message-list-bounce 1.2s infinite ease-in-out both;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &__timestamp {
    padding: 0 4px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);
  }

  &__retry {
    padding: 0;
    font-size: 0.75rem;
    color: var(--primary, #5C7B9D);
    text-decoration: underline;
    cursor: pointer;
    background: none;
    border: none;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 0 2px;
  }

  &__icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    min-height: 24px;
    color: var(--muted-foreground, #666E7A);
    cursor: pointer;
    background: none;
    border: none;
    border-radius: var(--radius-sm, 4px);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: var(--accent, #5C7B9D);
      color: var(--accent-foreground, #FFFFFF);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    // Rated/regenerate-eligible state - mirrors the React reference's active icon colour
    // (text-foreground rather than a brand colour).
    &[aria-pressed='true'] {
      color: var(--foreground, #4A5568);
    }
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    padding: 6px 12px;
    font-size: 0.75rem;
    color: var(--foreground, #4A5568);
    cursor: pointer;
    background-color: var(--card, #FFFFFF);
    border: 1px solid var(--border, #E0E2E5);
    border-radius: var(--radius-full, 9999px);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      background-color: var(--accent, #5C7B9D);
      color: var(--accent-foreground, #FFFFFF);
    }
  }

  &__tool-activity {
    padding: 0 4px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);
  }
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes help-message-list-bounce {

  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.6;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
